import { X } from 'lucide-react'
import type { UserSettings } from '@fuelfinder/shared'
import { SpendSelector } from './SpendSelector'
import { RadiusSelector } from './RadiusSelector'

interface SettingsModalProps {
  open: boolean
  onClose: () => void
  settings: UserSettings
  updateSettings: (partial: Partial<UserSettings>) => void
}

/**
 * Settings modal - changes apply immediately
 */
export function SettingsModal({ open, onClose, settings, updateSettings }: SettingsModalProps) {

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Settings
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 min-h-[44px] rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close settings"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Typical Spend Section */}
          <section>
            <label className="block text-sm text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium mb-2">
              Typical Fill-up Spend
            </label>
            <SpendSelector
              value={settings.typicalSpendPounds}
              onChange={(typicalSpendPounds) => updateSettings({ typicalSpendPounds })}
            />
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              Used to estimate refill cost
            </p>
          </section>

          {/* Search Radius Section */}
          <section>
            <label className="block text-sm text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium mb-2">
              Search Radius
            </label>
            <RadiusSelector
              value={settings.radiusMiles}
              onChange={(radiusMiles) => updateSettings({ radiusMiles })}
            />
          </section>
        </div>

        {/* Footer with close button */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="w-full py-3 px-4 rounded-lg text-white bg-green-600 hover:bg-green-700 font-medium transition-colors"
          >
            Done
          </button>
        </div>

        {/* Attribution footer */}
        <div className="px-4 pb-4 text-center space-y-1">
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Fuel-Prices v1.0
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Contains OS data &copy; Crown copyright and database right 2026
          </p>
        </div>
      </div>
    </div>
  )
}
